import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ChevronRight, Trash2, Link2, Loader2 } from "lucide-react";
import { api, API, formatApiError, formatDateShort, isOverdue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeading from "@/components/PageHeading";
import PipelinePill from "@/components/PipelinePill";

export default function Tasks() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/tasks", { params: { brand_id: brandId || undefined } })
      .then(({ data }) => setTasks(data))
      .finally(() => setLoading(false));
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const advance = async (t) => {
    if (t.phase >= 5) return;
    try {
      await api.patch(`/tasks/${t.id}`, { phase: t.phase + 1 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare la task "${t.title}"?`)) return;
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  const copyUserIcs = () => {
    if (!user?.ics_token) return;
    navigator.clipboard.writeText(`${API}/ics/user/${user.ics_token}.ics`);
    toast.success("Link ICS copiato. Abbonati una volta da Apple Calendario per vedere le task.");
  };

  const bmap = Object.fromEntries(brands.map((b) => [b.id, b.name]));

  return (
    <div data-testid="tasks-page">
      <PageHeading kicker="04 · OPERATIVITÀ" title="Task"
        subtitle="Generate automaticamente dal PED. Avanza ogni task lungo la pipeline a cinque fasi." />

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="tasks-brand-select" className="field-line max-w-[220px] bg-transparent">
          <option value="">Tutti i brand</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button onClick={copyUserIcs} data-testid="copy-user-ics-button" className="inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
          <Link2 size={13} strokeWidth={1.5} /> Feed calendario (ICS)
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-stone2e" size={24} /></div>
      ) : tasks.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-12 text-center">Nessuna task. Pubblica un contenuto dal PED per generarle automaticamente.</p>
      ) : (
        <div className="space-y-px bg-black/10 border border-black/10">
          {tasks.map((t) => {
            const overdue = isOverdue(t.due_date) && t.phase < 5;
            return (
              <div key={t.id} data-testid={`task-row-${t.id}`} className={`bg-greige/60 hover:bg-white/60 transition-colors p-5 ${overdue ? "border-l-2 border-l-black" : ""}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="md:w-2/5 min-w-0">
                    <p className="font-body text-sm text-ink font-medium truncate">{t.title}</p>
                    <p className="font-body text-xs text-stone2e truncate mt-0.5">Contenuto: {t.item_title}</p>
                    <p className="meta-label mt-1">
                      {(bmap[t.brand_id] || "").toUpperCase()} · SCADENZA {formatDateShort(t.due_date).toUpperCase()}{overdue ? " · IN RITARDO" : ""}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0"><PipelinePill phase={t.phase} /></div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.phase < 5 && (
                      <button onClick={() => advance(t)} data-testid={`advance-task-${t.id}`}
                        className="inline-flex items-center gap-1.5 bg-black text-greige px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-stone2e transition-colors">
                        Avanza <ChevronRight size={13} strokeWidth={1.75} />
                      </button>
                    )}
                    <button onClick={() => remove(t)} data-testid={`delete-task-${t.id}`} className="text-stone2e hover:text-ink p-2"><Trash2 size={15} strokeWidth={1.5} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
